import React, { useState, useRef } from 'react';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { simulationService } from '../../services/simulationService';
import MaterialSpecificationModal from './MaterialSpecificationModal';

const MaterialSpecificationUploader = ({ materialName, projectName, projectLocation, onTransferValues, onMaterialDichteApplied, onBeforeUpload }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset previous results
    setUploadError(null);
    setUploadResult(null);
    setIsUploading(true);
    setShowModal(true);

    try {
      // Validate file
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      if (file.size > 16 * 1024 * 1024) {
        throw new Error('File size must be less than 16MB');
      }

      // Upload file
      const result = await simulationService.uploadMaterialSpecification(
        file,
        projectName || 'Simulation Project'
      );

      if (result.success) {
        setUploadResult(result.data);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    // Associate the upload with this card even if the user didn't click it first
    onBeforeUpload?.();
    fileInputRef.current?.click();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUploadResult(null);
    setUploadError(null);
  };

  return (
    <>
      {/* Upload Button */}
      <button
        onClick={handleUploadClick}
        disabled={isUploading}
        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Upload Material Specification (Prüfzeugnis)"
      >
        {isUploading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
        ) : (
          <Upload className="h-4 w-4" />
        )}
      </button>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Results Modal */}
      <MaterialSpecificationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        data={uploadResult}
        isLoading={isUploading}
        error={uploadError}
        onTransferValues={onTransferValues}
        initialConstructionSite={projectLocation}
        materialName={materialName}
        projectName={projectName}
        onMaterialDichteApplied={onMaterialDichteApplied}
      />
    </>
  );
};

export default MaterialSpecificationUploader;
